/**
 * Channel/PWA Type Overrides
 *
 * This file is for overriding/correcting the swagger-generated types. When overriding a type:
 * 1. Import the incorrect type without "Dto" so that the original name can be used for export
 * 2. Add a jsdoc-style comment with the name of the original type (just for file readability)
 * 3. Create new overridden type and export it using the original name from data-contracts
 * 4. Explicitly re-export the new type from index.ts in this folder (necessary to avoid ambiguity)
 */
import {
    AchBatchFilterModelDto as AchBatchFilterModel,
    AchPaymentFilterModelDto as AchPaymentFilterModel,
    CustomerModelDto,
    ErrorResponseModelDto,
    GetItemsResponseModelDto as GetItemsResponseWithoutCustomerModelDto,
    InstitutionModelDto as InstitutionModel,
    InternalTransferAddTransactionModelDto as InternalTransferAddTransactionModel,
    ItemModelDto as ItemWithoutCustomerModelDto,
    InternalTransferAddTransactionDetailModelDto as InternalTransferAddTransactionDetailModel,
} from './generated/data-contracts';

import { InternalTransfersClient as GeneratedInternalTransfersClient } from './generated/clients';

import { Injectable } from '@jack-henry/frontend-utils/di';
import { ContentType, RequestParams } from './generated/http-client';
import { AddTransactionRequest } from '@treasury/domain/transfers';

/**
 * AchBatchFilterModelDto
 */

type RequiredAchBatchFilterModelProps =
    | 'achCompanyList'
    | 'batchName'
    | 'creditAmountType'
    | 'debitAmountType'
    | 'secCode'
    | 'status'
    | 'taxTemplateType'
    | 'updatedDateType';

export type AchBatchFilterModelDto = Pick<AchBatchFilterModel, RequiredAchBatchFilterModelProps> &
    Partial<Exclude<AchBatchFilterModel, RequiredAchBatchFilterModelProps>>;

/**
 * AchPaymentFilterModelDto
 */

type RequiredAchPaymentFilterModelProps =
    | 'achCompanyList'
    | 'batchName'
    | 'creditAmountType'
    | 'debitAmountType'
    | 'secCode'
    | 'status';

export type AchPaymentFilterModelDto = Pick<
    AchPaymentFilterModel,
    RequiredAchPaymentFilterModelProps
> &
    Partial<Exclude<AchPaymentFilterModel, RequiredAchPaymentFilterModelProps>>;

/**
 * ItemModelDto
 */

export interface ItemModelDto extends ItemWithoutCustomerModelDto {
    customer?: CustomerModelDto;
    frontImage: string;
    rearImage: string;
}

/**
 * GetItemsResponseModelDto
 */

export type GetItemsResponseModelDto = GetItemsResponseWithoutCustomerModelDto & {
    items: ItemModelDto[];
};

/**
 * InstitutionModelDto
 */

export enum PaymentDateModelDto {
    None = 0,
    ProcessDate = 1,
    DueDate = 2,
}

/**
 * InstitutionModelDto
 */

export interface InstitutionModelDto extends Omit<InstitutionModel, 'paymentDateModel'> {
    paymentDateModel: PaymentDateModelDto;
}

/**
 * InternalTransferAddTransactionDetailModelDto,
 */

// paymentOptionType is listed as required in the generated code, but it is optional in the API
export interface InternalTransferAddTransactionDetailModelDto
    extends Omit<InternalTransferAddTransactionDetailModel, 'paymentOptionType'> {
    paymentOptionType?: InternalTransferAddTransactionDetailModel['paymentOptionType'];
}

/**
 * InternalTransfersClient
 */

@Injectable()
export class InternalTransfersClient extends GeneratedInternalTransfersClient {
    // @ts-expect-error
    internalTransfersPost = (
        query: {
            type: string | null;
        },
        internalTransfer: AddTransactionRequest,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/InternalTransfers`,
            method: 'POST',
            query: query,
            body: internalTransfer,
            format: 'json',
            ...params,
        });

    // @ts-expect-error
    internalTransfersSaveTransferTemplate = (
        query: {
            type: string | null;
        },
        transferTemplate: AddTransactionRequest,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/internalTransfers/saveTransferTemplate`,
            method: 'POST',
            query: query,
            body: transferTemplate,
            format: 'json',
            ...params,
        });

    internalTransfersUpdateTransferTemplate = (
        transferTemplate: AddTransactionRequest | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/internalTransfers/updateTransferTemplate`,
            method: 'PUT',
            body: transferTemplate,
            type: ContentType.Json,
            format: 'json',
            ...params,
        });
}
